import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { SlideWrapper, staggerContainer, fadeUpItem, scaleUpItem } from '../Shared';
import { AlertCircle, XCircle, Zap, ShieldAlert, Database, Clock, LayoutGrid, MessageSquare, FolderOpen, PenTool, RefreshCw, FileText, CheckSquare } from 'lucide-react';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/v9ygAz6j1H3a29mCiVRRuK/vexu_logo.svg';
const NEXA_LOGO = 'https://cdn.magicpatterns.com/uploads/fKbS8KZb8wagSLFNLZymLJ/nexa42_png.png';
export function Slide1() {
  return <SlideWrapper className="items-center text-center" data-id="element-22">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="max-w-4xl flex flex-col items-center" data-id="element-23">
        <motion.div variants={scaleUpItem} className="mb-6 md:mb-8" data-id="element-24">
          <img src={VEXU_LOGO} alt="Vexu AI Logo" className="w-24 h-24 md:w-40 md:h-40 object-contain drop-shadow-sm" data-id="element-25" />
        </motion.div>
        <motion.div variants={scaleUpItem} className="mb-4 md:mb-6 inline-block" data-id="element-26">
          <div className="px-3 py-1 md:px-4 md:py-1.5 rounded-full border border-vexu-500/30 bg-vexu-500/10 text-vexu-600 text-xs md:text-sm font-semibold tracking-widest uppercase backdrop-blur-md" data-id="element-27">
            Welcome to the Future
          </div>
        </motion.div>
        <motion.h1 variants={fadeUpItem} className="text-5xl md:text-8xl font-heading font-bold mb-4 md:mb-6 tracking-tight text-charcoal-900" data-id="element-28">
          Vexu <span className="text-vexu-500" data-id="element-29">AI</span>
        </motion.h1>
        <motion.h2 variants={fadeUpItem} className="text-2xl md:text-4xl font-medium text-gray-700 mb-6 md:mb-8 font-heading" data-id="element-30">
          The Operating System for Intelligent Businesses
        </motion.h2>
        <motion.p variants={fadeUpItem} className="text-base md:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-31">
          AI-powered communication, automation, and unified operations.
        </motion.p>
      </motion.div>
    </SlideWrapper>;
}
export function Slide2() {
  const painPoints = ['Companies rely on multiple disconnected tools', 'Communication, documents, workflows, and tasks are separated', 'No unified visibility or control'];
  const results = [{
    icon: AlertCircle,
    text: 'High cost',
    color: 'text-red-500'
  }, {
    icon: Clock,
    text: 'Operational inefficiency',
    color: 'text-orange-500'
  }, {
    icon: Database,
    text: 'Data silos',
    color: 'text-yellow-600'
  }, {
    icon: Zap,
    text: 'Slow execution',
    color: 'text-purple-500'
  }];
  return <SlideWrapper data-id="element-32">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-33">
        <div data-id="element-34">
          <motion.h2 variants={fadeUpItem} className="text-3xl md:text-5xl font-heading font-bold mb-6 md:mb-8 leading-tight text-charcoal-900" data-id="element-35">
            Businesses Are Running on{' '}
            <span className="text-red-500" data-id="element-36">Fragmented Systems</span>
          </motion.h2>
          <div className="space-y-4 md:space-y-6" data-id="element-37">
            {painPoints.map((point, i) => <motion.div key={i} variants={fadeUpItem} className="flex items-start gap-3 md:gap-4" data-id="element-38">
                <div className="mt-1 p-1 md:p-1.5 rounded-full bg-red-100 text-red-500 shrink-0" data-id="element-39">
                  <XCircle className="w-4 h-4 md:w-5 md:h-5" data-id="element-40" />
                </div>
                <p className="text-base md:text-xl text-gray-700" data-id="element-41">{point}</p>
              </motion.div>)}
          </div>
        </div>
        <motion.div variants={scaleUpItem} className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-42">
          {results.map((res, i) => <div key={i} className="p-4 md:p-8 rounded-2xl bg-white border border-gray-100 shadow-sm flex flex-col items-center text-center gap-3 md:gap-4 hover:shadow-md transition-shadow" data-id="element-43">
              <res.icon className={`w-8 h-8 md:w-12 md:h-12 ${res.color}`} data-id="element-44" />
              <h3 className="text-sm md:text-lg font-semibold text-charcoal-900" data-id="element-45">
                {res.text}
              </h3>
            </div>)}
        </motion.div>
      </motion.div>
    </SlideWrapper>;
}
export function Slide3() {
  const stack = [{
    category: 'Communication',
    tools: 'Slack, Microsoft Teams',
    icon: MessageSquare,
    iconColor: 'text-blue-500'
  }, {
    category: 'Document Management',
    tools: 'Google Drive, Dropbox',
    icon: FolderOpen,
    iconColor: 'text-amber-500'
  }, {
    category: 'Digital Signature',
    tools: 'DocuSign, Adobe Sign',
    icon: PenTool,
    iconColor: 'text-purple-500'
  }, {
    category: 'Workflow Automation',
    tools: 'Zapier, Pipefy',
    icon: RefreshCw,
    iconColor: 'text-orange-500'
  }, {
    category: 'Forms',
    tools: 'Typeform, Jotform',
    icon: FileText,
    iconColor: 'text-pink-500'
  }, {
    category: 'Task Management',
    tools: 'Monday.com, Asana',
    icon: CheckSquare,
    iconColor: 'text-red-500'
  }];
  return <SlideWrapper data-id="element-46">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="h-full flex flex-col justify-center pt-0 pb-0 mt-0 mb-0" data-id="element-47">
        <motion.h2 variants={fadeUpItem} className="md:text-4xl font-heading font-bold mb-2 text-charcoal-900 text-[1.6875rem]" data-id="element-48">
          The Current Stack
        </motion.h2>
        <motion.p variants={fadeUpItem} className="md:text-xl text-gray-500 mb-6 md:mb-8 text-[1rem]" data-id="element-49">
          A Company Today Uses Multiple Systems
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8" data-id="element-50">
          <motion.div variants={fadeUpItem} className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4" data-id="element-51">
            {stack.map((item, i) => <div key={i} className="p-4 md:p-5 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center gap-3 md:gap-4 pt-[0.75rem] pb-[0.75rem]" data-id="element-52">
                <div className="flex-1 flex flex-col gap-1" data-id="element-53">
                  <span className="md:text-sm text-gray-500 uppercase tracking-wider text-[0.875rem] font-[500]" data-id="element-54">
                    {item.category}
                  </span>
                  <span className="md:text-lg text-charcoal-900 text-[1rem]" data-id="element-55">
                    {item.tools}
                  </span>
                </div>
                <item.icon className={`w-6 h-6 md:w-8 md:h-8 ${item.iconColor} shrink-0 opacity-60`} data-id="element-56" />
              </div>)}
          </motion.div>
          <motion.div variants={scaleUpItem} className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8 flex flex-col justify-center items-center text-center shadow-sm" data-id="element-57">
            <ShieldAlert className="w-12 h-12 md:w-16 md:h-16 text-amber-500 mb-3 md:mb-4" data-id="element-58" />
            <h3 className="text-xl md:text-2xl font-heading font-bold text-charcoal-900 mb-1 md:mb-2" data-id="element-59">
              Key Insight
            </h3>
            <p className="text-2xl md:text-3xl font-bold text-amber-600" data-id="element-60">
              6–10 systems
            </p>
            <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2" data-id="element-61">
              per company on average
            </p>
          </motion.div>
        </div>

        {/* Fragmented Tools Visual */}
        <motion.div variants={fadeUpItem} className="mt-2 md:mt-4 p-4 md:p-5 rounded-2xl bg-white border border-red-100 shadow-sm" data-id="element-62">
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-2 md:gap-3" data-id="element-63">
            {[{
            name: 'Slack',
            color: 'bg-purple-100 text-purple-600 border-purple-200'
          }, {
            name: 'Drive',
            color: 'bg-blue-100 text-blue-600 border-blue-200'
          }, {
            name: 'DocuSign',
            color: 'bg-yellow-100 text-yellow-700 border-yellow-200'
          }, {
            name: 'Zapier',
            color: 'bg-orange-100 text-orange-600 border-orange-200'
          }, {
            name: 'Typeform',
            color: 'bg-pink-100 text-pink-600 border-pink-200'
          }, {
            name: 'Asana',
            color: 'bg-red-100 text-red-600 border-red-200'
          }].map((tool, i) => <div key={i} className="flex flex-col md:flex-row items-center gap-2" data-id="element-64">
                <div className={`px-3 py-2 rounded-lg border text-sm font-semibold ${tool.color}`} data-id="element-65">
                  {tool.name}
                </div>
                {i < 5 && <div className="hidden md:flex items-center gap-1 text-red-300" data-id="element-66">
                    <div className="w-4 h-0.5 bg-red-300 rounded" data-id="element-67" />
                    <span className="text-red-400 text-xs font-bold" data-id="element-68">✕</span>
                    <div className="w-4 h-0.5 bg-red-300 rounded" data-id="element-69" />
                  </div>}
                {i < 5 && <div className="flex md:hidden items-center gap-1 text-red-300" data-id="element-70">
                    <div className="h-4 w-0.5 bg-red-300 rounded" data-id="element-71" />
                    <span className="text-red-400 text-xs font-bold" data-id="element-72">✕</span>
                    <div className="h-4 w-0.5 bg-red-300 rounded" data-id="element-73" />
                  </div>}
              </div>)}
          </div>
          <p className="text-center text-xs md:text-sm text-red-400 font-medium mt-3" data-id="element-74">
            No integration · No shared data · No unified view
          </p>
        </motion.div>
      </motion.div>
    </SlideWrapper>;
}
export function Slide4() {
  const gaps = ['No unified system', 'No AI-driven execution', 'No real enterprise hierarchy support', 'No end-to-end automation'];
  return <SlideWrapper className="items-center text-center" data-id="element-75">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="max-w-4xl w-full" data-id="element-76">
        <motion.h2 variants={fadeUpItem} className="text-3xl md:text-5xl font-heading font-bold mb-10 md:mb-16 text-charcoal-900" data-id="element-77">
          What's Missing Today
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-78">
          {gaps.map((gap, i) => <motion.div key={i} variants={scaleUpItem} className="p-5 md:p-8 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center gap-4 md:gap-6 text-left group hover:border-red-300 transition-colors" data-id="element-79">
              <div className="p-3 md:p-4 rounded-full bg-red-50 text-red-500 group-hover:bg-red-100 transition-colors shrink-0" data-id="element-80">
                <XCircle className="w-6 h-6 md:w-8 md:h-8" data-id="element-81" />
              </div>
              <p className="text-lg md:text-2xl font-medium text-charcoal-900" data-id="element-82">
                {gap}
              </p>
            </motion.div>)}
        </div>
      </motion.div>
    </SlideWrapper>;
}
export function Slide5() {
  return <SlideWrapper className="items-center text-center" data-id="element-83">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="max-w-5xl w-full" data-id="element-84">
        <motion.h2 variants={fadeUpItem} className="text-3xl md:text-5xl font-heading font-bold mb-3 md:mb-4 text-charcoal-900" data-id="element-85">
          Vexu AI Ecosystem
        </motion.h2>
        <motion.p variants={fadeUpItem} className="text-lg md:text-2xl text-vexu-600 mb-10 md:mb-16 font-heading" data-id="element-86">
          Two Products. One Vision.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10 md:mb-16" data-id="element-87">
          <motion.div variants={scaleUpItem} className="relative p-6 md:p-10 rounded-3xl overflow-hidden group bg-white shadow-sm border border-gray-100" data-id="element-88">
            <img src={VEXU_LOGO} alt="Vexu AI" className="w-12 h-12 md:w-16 md:h-16 object-contain mx-auto mb-3 md:mb-4 relative z-10" data-id="element-89" />
            <h3 className="text-2xl md:text-3xl font-bold font-heading mb-2 md:mb-4 text-charcoal-900 relative z-10" data-id="element-90">
              Vexu AI
            </h3>
            <p className="text-base md:text-xl text-gray-600 relative z-10" data-id="element-91">
              Intelligence & Automation
            </p>
          </motion.div>

          <motion.div variants={scaleUpItem} className="relative p-6 md:p-10 rounded-3xl overflow-hidden group bg-white shadow-sm border border-gray-100" data-id="element-92">
            <img src={NEXA_LOGO} alt="Nexa42" className="h-14 md:h-20 object-contain mx-auto mb-3 md:mb-4 relative z-10" data-id="element-93" />
            <p className="text-base md:text-xl text-gray-600 relative z-10" data-id="element-94">
              Business Operating System
            </p>
          </motion.div>
        </div>

        <motion.div variants={fadeUpItem} className="inline-block p-5 md:p-8 rounded-2xl bg-white shadow-sm border border-gray-100" data-id="element-95">
          <p className="text-lg md:text-3xl font-medium font-heading italic text-charcoal-900" data-id="element-96">
            "One platform to run your business.{' '}
            <span className="text-vexu-600" data-id="element-97">One AI to automate it.</span>"
          </p>
        </motion.div>
      </motion.div>
    </SlideWrapper>;
}